import ApiClient from '@/api/ApiClient';

import type {
  Asset,
  FilterCriteria,
  PagedResultAsset,
  SendAssetRequest,
  SortCriteria,
  StatisticsResult,
} from '../models';

export type GetAssetsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export type GetAssetStatisticsRequest = {
  employee?: string;
  employeeDepartmentIds?: string[];
  filterCriteria?: FilterCriteria[];
  groupBy?: string;
  includeLimit?: boolean;
  limit?: number;
  skip?: number;
  sortCriteria?: SortCriteria[];
};

export class AssetsClient {
  apiClient: ApiClient;

  constructor(apiClient?: ApiClient) {
    this.apiClient = apiClient || ApiClient.instance;
  }

  getAssets(request: GetAssetsRequest = {}): Promise<PagedResultAsset> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/assets',
      params,
    });
  }

  addAsset(request: { body?: Asset; } = {}): Promise<Asset> {
    const { body } = request;
    return this.apiClient.put({
      path: '/api/assets',
      body,
    });
  }

  createAsset(request: { body?: Asset; } = {}): Promise<Asset> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/assets',
      body,
    });
  }

  assign(request: { body?: SendAssetRequest; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/assets/assign',
      body,
    });
  }

  getAssetByKey(request: { key?: string; } = {}): Promise<Asset> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/assets/by-key',
      params,
    });
  }

  notifyGroup(request: { body?: SendAssetRequest; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/assets/notify-group',
      body,
    });
  }

  retrieve(request: { body?: SendAssetRequest; } = {}): Promise<void> {
    const { body } = request;
    return this.apiClient.post({
      path: '/api/assets/retrieve',
      body,
    });
  }

  getAssetStatistics(request: GetAssetStatisticsRequest = {}): Promise<StatisticsResult[]> {
    const { ...params } = request;
    return this.apiClient.get({
      path: '/api/assets/statistics',
      params,
    });
  }

  getAsset(request: { id: number; }): Promise<Asset> {
    const { id } = request;
    return this.apiClient.get({
      path: `/api/assets/${id}`,
    });
  }

  exportAssets(filter: string = '', request: GetAssetsRequest = {}): Promise<any> {
    return this.apiClient.getBlob({
      path: `/api/assets/export?${filter ?? ''}`,
      params: request,
    });
  }
}

export default AssetsClient;
